"use client";

import { useEffect, useState } from "react";
import { SIGNUP_FIELDS, GOOGLE_ADS } from "@/lib/site";
import { track, googleConversion, identify, phContext } from "@/lib/analytics";

type Status = "idle" | "loading" | "done" | "error";

function utm(key: string): string {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get(key) || "";
}

export function SignupForm({ compact = false, tier }: { compact?: boolean; tier?: string }) {
  const [status, setStatus] = useState<Status>("idle");
  const [started, setStarted] = useState(false);
  const [email, setEmail] = useState("");
  const [trade, setTrade] = useState("");
  const [size, setSize] = useState("");
  const [role, setRole] = useState("");

  // Prefill the trade from the preview studio ("Preview my RAMS"): read what was stored
  // and listen for live selection changes so the form continues the chosen-trade journey.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = window.sessionStorage.getItem("ramsdocs_trade");
      if (stored) setTrade(stored);
    } catch {
      /* ignore */
    }
    const onTrade = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      if (detail) setTrade(detail);
    };
    window.addEventListener("ramsdocs:trade", onTrade);
    return () => window.removeEventListener("ramsdocs:trade", onTrade);
  }, []);

  function markStarted() {
    if (started) return;
    setStarted(true);
    track("preview_signup_started", { trade, pricing_tier_clicked: tier || "" });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.includes("@")) return;
    markStarted();
    setStatus("loading");
    const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
    const body = {
      email: email.trim().toLowerCase(),
      source: typeof window !== "undefined" ? window.location.hostname.replace(/^www\./, "") : "ramsdocs.co.uk",
      intent: "early_access",
      industry: trade,
      company_size: size === "Just me" ? "just-me" : size,
      role: role === "Owner / Director" ? "owner" : role === "Site / Contracts Manager" ? "manager" : role === "Health & Safety" ? "healthsafety" : role.toLowerCase(),
      pricing_tier_clicked: tier || "",
      landing_page: typeof window !== "undefined" ? window.location.pathname : "/",
      utm_source: utm("utm_source"),
      utm_medium: utm("utm_medium"),
      utm_campaign: utm("utm_campaign"),
      utm_term: utm("utm_term"),
      utm_content: utm("utm_content"),
      gclid: params.get("gclid") || "",
      msclkid: params.get("msclkid") || "",
      // Attribute the server-side signup_completed to this browser person + session.
      ...phContext(),
    };
    try {
      const res = await fetch("/api/signup", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (res.ok) {
        setStatus("done");
        // Merge the anonymous browser person into the email-keyed person (matching the
        // server's signup_completed distinct_id) so the pre-signup journey is attributed.
        identify(body.email, { email: body.email, industry: body.industry, role: body.role, company_size: body.company_size, intent: "early_access" });
        // signup_completed is emitted server-side from the worker (durable-row gated);
        // this is the front-end journey marker for the preview funnel.
        track("preview_signup_completed", { trade, company_size: body.company_size, role: body.role });
        googleConversion(GOOGLE_ADS.signupSendTo);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  }

  if (status === "done") {
    return (
      <div className="rounded-xl border border-emerald/30 bg-emerald-tint p-6 text-center">
        <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-emerald text-white">
          <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="m5 13 4 4L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </div>
        <p className="mt-3 font-display text-lg font-bold text-ink">Got it — we’ll be in touch.</p>
        <p className="mt-1 text-sm text-body">
          We’ll email <span className="font-semibold text-ink">{email}</span>
          {trade ? <> a <span className="font-semibold text-ink">{trade}</span> RAMS draft for your job</> : <> your RAMS draft</>} to review. Free during early access, with founder-led setup support.
        </p>
      </div>
    );
  }

  const field = "w-full rounded-lg border border-line bg-white px-4 py-3 text-ink outline-none transition-colors placeholder:text-muted/70 focus:border-emerald focus:ring-2 focus:ring-emerald/15";

  return (
    <form onSubmit={submit} onFocus={markStarted} className="space-y-3">
      <div className={compact ? "" : "grid gap-3 sm:grid-cols-2"}>
        {!compact && (
          <select value={trade} onChange={(e) => setTrade(e.target.value)} required className={field} aria-label="Your trade">
            <option value="">Your trade</option>
            {SIGNUP_FIELDS.trade.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Work email" aria-label="Work email" className={field} />
      </div>
      {!compact && (
        <div className="grid gap-3 sm:grid-cols-2">
          <select value={size} onChange={(e) => setSize(e.target.value)} className={field}>
            <option value="">Company size (optional)</option>
            {SIGNUP_FIELDS.companySize.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={role} onChange={(e) => setRole(e.target.value)} className={field}>
            <option value="">Your role (optional)</option>
            {SIGNUP_FIELDS.role.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      )}
      <button
        type="submit"
        disabled={status === "loading"}
        className="w-full rounded-full bg-emerald px-5 py-3.5 font-semibold text-white shadow-[0_8px_20px_-8px_rgba(27,163,123,0.7)] transition-colors hover:bg-emerald-deep disabled:opacity-60"
      >
        {status === "loading" ? "Preparing your draft…" : "Show my RAMS draft"}
      </button>
      {status === "error" && <p role="alert" className="text-sm text-risk-veryhigh">Something went wrong — please try again or email us.</p>}
      <p className="text-center text-xs text-muted">Free during early access — no card required. We’ll only email you about RamsDocs.</p>
    </form>
  );
}
